import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { cx } from './cx';

interface C1ActionCardBase {
  icon?: ReactNode;
  label: ReactNode;
  description?: ReactNode;
  /** Keyboard shortcut shown on the right, e.g. "Ctrl K". */
  hint?: ReactNode;
  /** Count shown on the right; suppressed when zero or undefined. */
  badge?: number;
  className?: string;
}

type AsButton = C1ActionCardBase &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof C1ActionCardBase> & { to?: undefined };

type AsLink = C1ActionCardBase &
  Omit<LinkProps, keyof C1ActionCardBase | 'to'> & { to: LinkProps['to'] };

export type C1ActionCardProps = AsButton | AsLink;

/** A tappable card: icon, label, optional description, and a hint or badge. */
export function C1ActionCard(props: C1ActionCardProps) {
  const { icon, label, description, hint, badge, className } = props;
  const classes = cx('c1-actionCard', className);

  const content = (
    <>
      {icon ? (
        <span className="c1-actionCardIcon" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <span className="c1-actionCardText">
        <span className="c1-actionCardLabel">{label}</span>
        {description ? <span className="c1-actionCardDescription">{description}</span> : null}
      </span>
      {badge ? (
        <span className="c1-actionCardBadge" aria-hidden="true">
          {badge}
        </span>
      ) : null}
      {hint && !badge ? (
        <kbd className="c1-actionCardHint" aria-hidden="true">
          {hint}
        </kbd>
      ) : null}
    </>
  );

  if ('to' in props && props.to !== undefined) {
    const {
      icon: _i,
      label: _l,
      description: _d,
      hint: _h,
      badge: _b,
      className: _c,
      to,
      ...linkRest
    } = props;
    return (
      <Link to={to} className={classes} {...linkRest}>
        {content}
      </Link>
    );
  }

  const {
    icon: _i,
    label: _l,
    description: _d,
    hint: _h,
    badge: _b,
    className: _c,
    type = 'button',
    ...buttonRest
  } = props as AsButton;

  return (
    <button type={type} className={classes} {...buttonRest}>
      {content}
    </button>
  );
}
